import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "./apiMedecins.js";

export const BOURSE_TRAVAIL_SECTEURS = [
  ["sect-port-royal", "Port Royal"],
  ["sect-digestif", "Digestif"],
  ["sect-reanimation", "Réanimation"],
  ["sect-thoracique", "Thoracique"],
  ["sect-orthopedie", "Orthopédie"],
  ["sect-oph", "Ophtalmologie"],
  ["sect-douleur", "Douleur"],
  ["sect-uca", "UCA"],
];

export const BOURSE_TRAVAIL_ENGAGEMENT =
  "Une disponibilité validée par un responsable devient engageante. Vous pouvez demander son annulation, mais vous restez engagé tant qu’un responsable du secteur retenu ne l’a pas acceptée.";

const LABEL_BY_SECTOR = Object.fromEntries(BOURSE_TRAVAIL_SECTEURS);

function formatDate(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T12:00:00Z`));
}

function statusLabel(proposition) {
  if (proposition.cancellation_requests?.some(r => r.status === "pending")) return "Annulation demandée";
  if (proposition.statut === "attribuee") {
    return `Attribuée à ${LABEL_BY_SECTOR[proposition.secteur_attribue_id] || proposition.secteur_attribue_id}`;
  }
  if (proposition.statut === "refusee") return "Refusée";
  if (proposition.statut === "expiree") return "Expirée";
  if (proposition.statut === "annulee") return "Attribution annulée";
  return "En attente";
}

function statusColors(statut) {
  if (statut === "attribuee") return { background: "#dcfce7", color: "#166534", border: "#86efac" };
  if (statut === "refusee") return { background: "#fee2e2", color: "#991b1b", border: "#fecaca" };
  return { background: "#fef3c7", color: "#92400e", border: "#fde68a" };
}

function tomorrowIso() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
}

const backToPortal = () => { window.location.href = "/portail-medecin"; };
const sectorDecisionLabel = (sector, proposition) => {
  if (sector.statut === "accepted") return proposition.statut === "annulee" ? "Retenu avant annulation" : "Retenu";
  if (sector.statut === "refused") return "Refusé";
  if (sector.statut === "closed_elsewhere") return "Fermé : autre secteur retenu";
  return proposition.statut === "expiree" ? "Sans décision à l’expiration" : "En attente";
};

function BourseTravailMedecin({ onBack = backToPortal }) {
  const [eligible, setEligible] = useState(null);
  const [dates, setDates] = useState([""]);
  const [secteurIds, setSecteurIds] = useState([]);
  const [preference, setPreference] = useState("disponible");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [propositions, setPropositions] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [message, setMessage] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [cancellationForm, setCancellationForm] = useState(null);
  const [cancellationBusy, setCancellationBusy] = useState(false);

  const normalizedDates = useMemo(
    () => [...new Set(dates.map((date) => String(date || "").trim()).filter(Boolean))].sort(),
    [dates]
  );
  const canReview = normalizedDates.length > 0 && secteurIds.length > 0 && !submitting;

  const loadPropositions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch("/api/v2/bourse-travail/mes-propositions", { credentials: "include" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Impossible de charger vos propositions.");
      setPropositions(Array.isArray(data.propositions) ? data.propositions : []);
      setHistorique(Array.isArray(data.historique) ? data.historique : []);
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiFetch("/api/v2/bourse-travail/feature-status", { credentials: "include" })
      .then(async response => ({ ok:response.ok, data:await response.json() }))
      .then(({ok,data}) => { if (!cancelled) { setEligible(ok && data.enabled === true); if (ok && data.enabled === true) loadPropositions(); } })
      .catch(() => { if (!cancelled) setEligible(false); });
    return () => { cancelled = true; };
  }, [loadPropositions]);

  const updateDate = (index, value) => {
    setConfirming(false);
    setDates((current) => current.map((date, dateIndex) => (dateIndex === index ? value : date)));
  };

  const updateSector = (secteurId, checked) => {
    setConfirming(false);
    setSecteurIds((current) =>
      checked
        ? [...new Set([...current, secteurId])]
        : current.filter((value) => value !== secteurId)
    );
  };

  const submit = async () => {
    setSubmitting(true);
    setMessage(null);
    const created = [];
    try {
      for (const date of normalizedDates) {
        const response = await apiFetch("/api/v2/bourse-travail/propositions", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ date, preference, secteur_ids: secteurIds }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(`${formatDate(date)} : ${data.error || "dépôt impossible"}`);
        }
        created.push(data.proposition);
      }
      setDates([""]);
      setSecteurIds([]);
      setPreference("disponible");
      setConfirming(false);
      const warnings = created.filter(p => ["failed","unconfirmed"].includes(p.notification?.state));
      setMessage({
        type: warnings.length ? "error" : "success",
        text: warnings.length ? warnings.map(p => p.notification.message).join(" ") : `${created.length} proposition(s) enregistrée(s).`,
      });
      await loadPropositions();
    } catch (error) {
      setMessage({
        type: "error",
        text: `${error.message}${created.length ? ` (${created.length} proposition(s) précédente(s) ont bien été enregistrées.)` : ""}`,
      });
      await loadPropositions();
    } finally {
      setSubmitting(false);
    }
  };

  const withdraw = async proposition => {
    if (!window.confirm(`Retirer votre proposition du ${formatDate(proposition.date)} ?`)) return;
    setWithdrawing(proposition.id);
    setMessage(null);
    try {
      const response = await apiFetch(`/api/v2/bourse-travail/propositions/${encodeURIComponent(proposition.id)}`, {
        method: "DELETE", credentials: "include",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Impossible de retirer cette proposition.");
      setMessage({ type: "success", text: "Proposition retirée. Son historique est conservé." });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      await loadPropositions();
      setWithdrawing(null);
    }
  };

  const cancellationAction = async (proposition, request = null) => {
    if (cancellationBusy) return;
    if (request && !window.confirm("Retirer votre demande d’annulation ? Vous restez engagé.")) return;
    setCancellationBusy(true);
    setMessage(null);
    try {
      const suffix = request ? `/${encodeURIComponent(request.id)}/withdraw` : "";
      const response = await apiFetch(`/api/v2/bourse-travail/propositions/${encodeURIComponent(proposition.id)}/cancellation-requests${suffix}`, {
        method:"POST", credentials:"include", headers:{"Content-Type":"application/json"},
        body:JSON.stringify(request ? {} : {reason:cancellationForm?.reason}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Impossible de traiter la demande.");
      const requests = data.proposition?.cancellation_requests || [];
      const notification = requests[requests.length - 1]?.notification;
      setMessage({type:["failed","unconfirmed"].includes(notification?.state) ? "error" : "success",
        text:[data.message, notification?.message].filter(Boolean).join(" ")});
      setCancellationForm(null);
    } catch (error) { setMessage({type:"error", text:error.message}); }
    finally { await loadPropositions(); setCancellationBusy(false); }
  };

  if (eligible !== true) return <div role="status">{eligible === null ? "Chargement..." : "La Bourse du travail n’est pas accessible à ce compte."} <button type="button" onClick={onBack}>Retour au portail</button></div>;

  return (
    <div style={styles.page}>
      <button type="button" onClick={onBack} style={styles.backButton}>← Retour au portail</button>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Bourse du travail</h2>
          <p style={styles.intro}>Proposez vos disponibilités futures aux secteurs du service.</p>
        </div>
      </div>

      {message && (
        <div role="status" style={{ ...styles.message, ...(message.type === "error" ? styles.error : styles.success) }}>
          {message.text}
        </div>
      )}

      <section style={styles.section} aria-labelledby="bourse-depot-title">
        <h3 id="bourse-depot-title" style={styles.sectionTitle}>Nouvelle proposition</h3>

        <div style={styles.fieldGroup}>
          <span style={styles.label}>Dates futures</span>
          {dates.map((date, index) => (
            <div key={index} style={styles.dateRow}>
              <input
                aria-label={`Date ${index + 1}`}
                type="date"
                min={tomorrowIso()}
                value={date}
                onChange={(event) => updateDate(index, event.target.value)}
                style={styles.input}
              />
              {dates.length > 1 && (
                <button
                  type="button"
                  aria-label={`Retirer la date ${index + 1}`}
                  onClick={() => {
                    setConfirming(false);
                    setDates((current) => current.filter((_, dateIndex) => dateIndex !== index));
                  }}
                  style={styles.iconButton}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setDates((current) => [...current, ""])} style={styles.secondaryButton}>
            + Ajouter une date
          </button>
          <span style={styles.hint}>Chaque date donnera lieu à une proposition indépendante.</span>
        </div>

        <fieldset style={styles.fieldset}>
          <legend style={styles.label}>Secteurs proposés</legend>
          <div style={styles.sectorGrid}>
            {BOURSE_TRAVAIL_SECTEURS.map(([id, label]) => (
              <label key={id} style={styles.choiceLabel}>
                <input
                  type="checkbox"
                  checked={secteurIds.includes(id)}
                  onChange={(event) => updateSector(id, event.target.checked)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset style={styles.fieldset}>
          <legend style={styles.label}>Niveau de préférence</legend>
          <div style={styles.preferenceRow}>
            <label style={styles.choiceLabel}>
              <input
                type="radio"
                name="bourse-preference"
                value="absolument"
                checked={preference === "absolument"}
                onChange={(event) => { setConfirming(false); setPreference(event.target.value); }}
              />
              Absolument
            </label>
            <label style={styles.choiceLabel}>
              <input
                type="radio"
                name="bourse-preference"
                value="disponible"
                checked={preference === "disponible"}
                onChange={(event) => { setConfirming(false); setPreference(event.target.value); }}
              />
              Simplement disponible
            </label>
          </div>
        </fieldset>

        {!confirming ? (
          <button type="button" disabled={!canReview} onClick={() => setConfirming(true)} style={styles.primaryButton}>
            Vérifier avant confirmation
          </button>
        ) : (
          <div style={styles.review}>
            <h4 style={styles.reviewTitle}>Récapitulatif avant confirmation</h4>
            <dl style={styles.summaryList}>
              <dt style={styles.summaryTerm}>Dates</dt>
              <dd style={styles.summaryValue}>{normalizedDates.map(formatDate).join(" ; ")}</dd>
              <dt style={styles.summaryTerm}>Secteurs</dt>
              <dd style={styles.summaryValue}>{secteurIds.map((id) => LABEL_BY_SECTOR[id]).join(" ; ")}</dd>
              <dt style={styles.summaryTerm}>Préférence</dt>
              <dd style={styles.summaryValue}>{preference === "absolument" ? "Absolument" : "Simplement disponible"}</dd>
            </dl>
            <p style={styles.warning}>{BOURSE_TRAVAIL_ENGAGEMENT}</p>
            <div style={styles.actions}>
              <button type="button" onClick={() => setConfirming(false)} disabled={submitting} style={styles.secondaryButton}>
                Modifier
              </button>
              <button type="button" onClick={submit} disabled={submitting} style={styles.primaryButton}>
                {submitting ? "Dépôt en cours..." : `Confirmer ${normalizedDates.length} proposition${normalizedDates.length > 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        )}
      </section>

      <section style={styles.section} aria-labelledby="bourse-list-title">
        <div style={styles.listHeader}>
          <h3 id="bourse-list-title" style={styles.sectionTitle}>Mes propositions</h3>
          <button type="button" onClick={loadPropositions} disabled={loading} style={styles.iconButton} aria-label="Actualiser les propositions">
            ↻
          </button>
        </div>
        {loading ? (
          <p style={styles.empty}>Chargement...</p>
        ) : propositions.length + historique.length === 0 ? (
          <p style={styles.empty}>Aucune proposition déposée.</p>
        ) : (
          <div style={styles.proposalList}>
            {[...propositions, ...historique].map((proposition, index) => {
              const colors = statusColors(proposition.statut);
              return (
                <React.Fragment key={proposition.id}>
                {index === propositions.length && historique.length > 0 && <h4>Historique des propositions clôturées</h4>}
                <article data-proposal-id={proposition.id} style={styles.proposalCard}>
                  <div style={styles.proposalTop}>
                    <strong>{formatDate(proposition.date)}</strong>
                    <span style={{ ...styles.statusBadge, background: colors.background, color: colors.color, borderColor: colors.border }}>
                      {statusLabel(proposition)}
                    </span>
                  </div>
                  {proposition.notification?.message && <p role={["failed","unconfirmed"].includes(proposition.notification.state) ? "alert" : "status"}
                    style={["failed","unconfirmed"].includes(proposition.notification.state) ? styles.warning : styles.hint}>{proposition.notification.message}</p>}
                  <div style={styles.proposalMeta}>
                    <span>{proposition.preference === "absolument" ? "Absolument" : "Simplement disponible"}</span>
                    <ul aria-label="Décisions par secteur">
                      {(proposition.secteurs || []).map(sector => <li key={sector.secteur_id}>
                        {LABEL_BY_SECTOR[sector.secteur_id] || sector.secteur_id} : <strong>{sectorDecisionLabel(sector, proposition)}</strong>
                      </li>)}
                    </ul>
                  </div>
                  {proposition.statut === "ouverte" && proposition.secteurs?.length > 0
                    && proposition.secteurs.every(sector => sector.statut === "refused")
                    && <p role="status">Tous les secteurs visés ont refusé. Votre proposition reste ouverte jusqu’à l’expiration de sa date ; vous pouvez la retirer.</p>}
                  {proposition.cancellation_message && <p role="status">{proposition.cancellation_message}</p>}
                  {proposition.cancellation_pending_message && <p role="status" style={styles.warning}>{proposition.cancellation_pending_message}</p>}
                  {(proposition.cancellation_requests || []).map(request => <div key={request.id} style={{marginTop:12, overflowWrap:"anywhere"}}>
                    <strong>{{pending:"Demande en attente", withdrawn:"Demande retirée", accepted:"Demande acceptée", refused:"Demande refusée"}[request.status]}</strong>
                    <p>Motif de votre demande : {request.reason}</p>
                    {request.decision_reason && <p>Motif du responsable : {request.decision_reason}</p>}
                    {request.status === "refused" && <p>{proposition.statut === "attribuee"
                      ? "Votre attribution est maintenue : vous restez engagé."
                      : "Au moment de ce refus, l’attribution était maintenue."}</p>}
                    {request.notification?.message && <p role={["failed","unconfirmed"].includes(request.notification.state) ? "alert" : "status"}>{request.notification.message}</p>}
                    {request.status === "pending" && <button type="button" style={styles.secondaryButton} disabled={cancellationBusy}
                      onClick={() => cancellationAction(proposition, request)}>Retirer ma demande d’annulation</button>}
                  </div>)}
                  {proposition.can_request_cancellation && (cancellationForm?.id === proposition.id
                    ? <form onSubmit={event => { event.preventDefault(); cancellationAction(proposition); }} style={{marginTop:12}}>
                      <label style={{display:"block"}}>Motif de la demande (obligatoire)
                        <textarea required maxLength={1000} value={cancellationForm.reason} disabled={cancellationBusy}
                          onChange={event => setCancellationForm({...cancellationForm, reason:event.target.value})}
                          style={{...styles.input, display:"block", width:"100%", boxSizing:"border-box", minHeight:80}} />
                      </label>
                      <p>Vous restez engagé tant qu’un responsable du secteur retenu n’a pas accepté votre demande.</p>
                      <div style={styles.actions}>
                        <button type="submit" style={styles.primaryButton} disabled={cancellationBusy || !cancellationForm.reason.trim()}>Envoyer la demande</button>
                        <button type="button" style={styles.secondaryButton} disabled={cancellationBusy} onClick={() => setCancellationForm(null)}>Fermer</button>
                      </div>
                    </form>
                    : <button type="button" style={{...styles.secondaryButton, marginTop:12}} disabled={cancellationBusy}
                      onClick={() => setCancellationForm({id:proposition.id, reason:""})}>Demander l’annulation</button>)}
                  {proposition.statut === "annulee" && <p>Motif : {proposition.cancellation_reason}</p>}
                  {proposition.statut === "expiree" && <p>Date dépassée : proposition conservée dans l’historique.</p>}
                  {proposition.statut === "ouverte" && <button type="button" style={styles.secondaryButton}
                    disabled={withdrawing !== null} onClick={() => withdraw(proposition)}>
                    {withdrawing === proposition.id ? "Retrait en cours..." : "Retirer ma proposition"}
                  </button>}
                </article>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// The portal's activity timer rerenders during native click capture.
// Keep controlled inputs stable until their own change handler has run.
export default React.memo(BourseTravailMedecin);

const styles = {
  page: { display: "flex", flexDirection: "column", gap: "18px", width: "100%", color: "#243b3b", background: "#ffffff", padding: "24px", boxSizing: "border-box", fontFamily: "Arial, sans-serif" },
  backButton: { alignSelf: "flex-start", border: "1px solid #475569", background: "#1e293b", color: "#f8fafc", padding: "10px 14px", borderRadius: "7px", cursor: "pointer", fontWeight: 700 },
  header: { display: "flex", justifyContent: "space-between", gap: "16px", alignItems: "start" },
  title: { margin: 0, color: "#174f4b", fontSize: "24px", letterSpacing: 0 },
  intro: { margin: "6px 0 0", color: "#526464" },
  section: { borderTop: "1px solid #475569", paddingTop: "18px", display: "flex", flexDirection: "column", gap: "16px" },
  sectionTitle: { margin: 0, color: "#243b3b", fontSize: "19px", letterSpacing: 0 },
  fieldGroup: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" },
  label: { color: "#243b3b", fontWeight: 700, fontSize: "15px" },
  dateRow: { display: "flex", gap: "8px", width: "100%", maxWidth: "390px" },
  input: { flex: 1, minWidth: 0, padding: "10px 12px", border: "1px solid #64748b", borderRadius: "6px", background: "#f8fafc", color: "#0f172a", fontSize: "15px" },
  iconButton: { width: "40px", height: "40px", border: "1px solid #64748b", borderRadius: "6px", background: "#334155", color: "#f8fafc", cursor: "pointer", fontSize: "20px" },
  secondaryButton: { border: "1px solid #64748b", borderRadius: "6px", background: "#334155", color: "#f8fafc", padding: "10px 14px", cursor: "pointer", fontWeight: 700 },
  hint: { color: "#526464", fontSize: "13px" },
  fieldset: { margin: 0, padding: 0, border: 0, minWidth: 0 },
  sectorGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "8px", marginTop: "9px" },
  preferenceRow: { display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "9px" },
  choiceLabel: { display: "flex", alignItems: "center", gap: "8px", padding: "10px", border: "1px solid #a5b8b5", borderRadius: "6px", background: "#eef5f3", color: "#243b3b", cursor: "pointer" },
  primaryButton: { alignSelf: "flex-start", border: 0, borderRadius: "6px", background: "#0ea5e9", color: "#082f49", padding: "11px 16px", cursor: "pointer", fontWeight: 800 },
  review: { border: "1px solid #38bdf8", background: "#0c4a6e", borderRadius: "7px", padding: "16px", display: "flex", flexDirection: "column", gap: "14px" },
  reviewTitle: { margin: 0, fontSize: "17px", color: "#f0f9ff" },
  summaryList: { margin: 0, display: "grid", gridTemplateColumns: "minmax(90px, auto) 1fr", gap: "8px 14px" },
  summaryTerm: { fontWeight: 800, color: "#bae6fd" },
  summaryValue: { margin: 0, color: "#f8fafc" },
  warning: { margin: 0, padding: "12px", border: "1px solid #fbbf24", borderRadius: "6px", background: "#451a03", color: "#fef3c7", fontWeight: 800, lineHeight: 1.5 },
  actions: { display: "flex", flexWrap: "wrap", gap: "10px" },
  listHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" },
  proposalList: { display: "grid", gap: "10px" },
  proposalCard: { border: "1px solid #a5b8b5", borderRadius: "7px", padding: "14px", background: "#f7faf9" },
  proposalTop: { display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px" },
  statusBadge: { border: "1px solid", borderRadius: "999px", padding: "5px 9px", fontSize: "13px", fontWeight: 800 },
  proposalMeta: { marginTop: "9px", display: "flex", flexDirection: "column", gap: "4px", color: "#526464", fontSize: "14px" },
  empty: { margin: 0, color: "#526464" },
  message: { border: "1px solid", borderRadius: "6px", padding: "11px 13px", fontWeight: 700 },
  success: { background: "#14532d", borderColor: "#4ade80", color: "#dcfce7" },
  error: { background: "#7f1d1d", borderColor: "#f87171", color: "#fee2e2" },
};
